import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * GroupDrawer — slide-out drawer for Add / Edit resident group.
 * Includes a searchable resident picker for resident assignment.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   group: object | null,         // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   onDelete: (group: object) => void,
 *   categories: Array<{ resident_group_category_id: number, resident_group_category_name: string }>,
 *   residents: Array               // Full MOCK_RESIDENTS array for the picker
 * }} props
 */
const GroupDrawer = ({ isOpen, onClose, group, onSave, onDelete, categories, residents }) => {
  const isEditMode = !!group;
  const drawerRef = useRef(null);

  // Form state
  const [groupName, setGroupName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [selectedResidentIds, setSelectedResidentIds] = useState([]);
  const [residentSearch, setResidentSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Populate form on open/group change
  useEffect(() => {
    if (isOpen && group) {
      setGroupName(group.resident_group_name || '');
      setCategoryId(group.resident_group_category_id?.toString() || '');
      setDescription(group.resident_description || '');
      setSelectedResidentIds((group.members || []).map((m) => m.resident_id));
      setResidentSearch('');
      setShowSelectedOnly(false);
      setShowDeleteConfirm(false);
    } else if (isOpen && !group) {
      setGroupName('');
      setCategoryId('');
      setDescription('');
      setSelectedResidentIds([]);
      setResidentSearch('');
      setShowSelectedOnly(false);
      setShowDeleteConfirm(false);
    }
  }, [isOpen, group]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Active residents only for the picker
  const activeResidents = useMemo(
    () => (residents || []).filter((r) => r.is_active),
    [residents]
  );

  // Filter residents by search and "show selected" toggle
  const filteredResidents = useMemo(() => {
    let list = activeResidents;
    if (showSelectedOnly) {
      list = list.filter((r) => selectedResidentIds.includes(r.resident_id));
    }
    if (residentSearch.trim()) {
      const s = residentSearch.toLowerCase();
      list = list.filter(
        (r) =>
          `${r.resident_first_name} ${r.resident_last_name}`.toLowerCase().includes(s) ||
          r.area_name?.toLowerCase().includes(s) ||
          r.area_type_description?.toLowerCase().includes(s) ||
          r.resident_nickname?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [activeResidents, residentSearch, showSelectedOnly, selectedResidentIds]);

  const handleResidentToggle = useCallback((residentId) => {
    setSelectedResidentIds((prev) =>
      prev.includes(residentId) ? prev.filter((id) => id !== residentId) : [...prev, residentId]
    );
  }, []);

  const handleSelectAllResidents = useCallback(() => {
    const allFilteredIds = filteredResidents.map((r) => r.resident_id);
    setSelectedResidentIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
  }, [filteredResidents]);

  const handleClearResidents = useCallback(() => {
    setSelectedResidentIds([]);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);

      const selectedCategory = categories.find((c) => c.resident_group_category_id.toString() === categoryId);

      const formData = {
        resident_group_id: group?.resident_group_id || null,
        resident_group_name: groupName.trim(),
        resident_description: description.trim(),
        resident_group_category_id: parseInt(categoryId, 10),
        resident_group_category_name: selectedCategory?.resident_group_category_name || '',
        members: activeResidents
          .filter((r) => selectedResidentIds.includes(r.resident_id))
          .map((r) => ({
            resident_id: r.resident_id,
            resident_name: `${r.resident_first_name} ${r.resident_last_name}`,
            area_name: r.area_name,
            area_type_name: r.area_type_name,
          })),
      };

      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    },
    [groupName, categoryId, description, selectedResidentIds, group, categories, activeResidents, onSave]
  );

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (group && onDelete) {
      onDelete(group);
    }
    setShowDeleteConfirm(false);
  }, [group, onDelete]);

  const isFormValid = groupName.trim() && categoryId;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="rp-drawer-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`rp-drawer rp-drawer--lg ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit Group' : 'Add Group'}
      >
        {/* Header */}
        <div className="rp-drawer__header">
          <h2 className="rp-drawer__title">
            {isEditMode ? 'Edit Group' : 'New Group'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rp-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="rp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="rp-form">
          <div className="rp-form__body">
            {/* Group Name */}
            <div>
              <label htmlFor="drawer-group-name" className="rp-field__label">
                Group Name <span className="rp-field__required">*</span>
              </label>
              <input
                type="text"
                id="drawer-group-name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                pattern="^[0-9a-zA-Z _\-]{1,100}$"
                className="rp-field__input"
                placeholder="Enter group name"
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="drawer-group-category" className="rp-field__label">
                Category <span className="rp-field__required">*</span>
              </label>
              <select
                id="drawer-group-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                required
                className="rp-field__select"
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat.resident_group_category_id} value={cat.resident_group_category_id}>
                    {cat.resident_group_category_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="drawer-group-description" className="rp-field__label">
                Description
              </label>
              <textarea
                id="drawer-group-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rp-field__textarea"
                placeholder="Optional description..."
                rows={3}
              />
            </div>

            {/* Resident Picker */}
            <div className="rp-resident-picker">
              <div className="rp-resident-picker__header">
                <p className="rp-field__label" style={{ marginBottom: 0 }}>
                  Residents
                  <span className="rp-resident-picker__count">
                    ({selectedResidentIds.length} selected)
                  </span>
                </p>
              </div>

              {/* Search */}
              <div className="rp-resident-picker__search">
                <i className="fe fe-search rp-resident-picker__search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search residents..."
                  value={residentSearch}
                  onChange={(e) => setResidentSearch(e.target.value)}
                  className="rp-resident-picker__search-input"
                />
              </div>

              {/* Select All / Clear / Show Selected */}
              <div className="rp-resident-picker__actions">
                <button
                  type="button"
                  onClick={handleSelectAllResidents}
                  className="rp-resident-picker__action-btn"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleClearResidents}
                  className="rp-resident-picker__action-btn"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setShowSelectedOnly((prev) => !prev)}
                  className={`rp-resident-picker__action-btn${showSelectedOnly ? ' rp-resident-picker__action-btn--active' : ''}`}
                >
                  Show Selected
                </button>
              </div>

              {/* Resident list */}
              <div className="rp-resident-picker__list">
                {filteredResidents.length === 0 ? (
                  <p className="rp-resident-picker__empty">
                    {showSelectedOnly ? 'No selections.' : 'No residents found'}
                  </p>
                ) : (
                  filteredResidents.map((r) => {
                    const isChecked = selectedResidentIds.includes(r.resident_id);
                    return (
                      <label
                        key={r.resident_id}
                        className={`rp-resident-picker__item ${isChecked ? 'is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleResidentToggle(r.resident_id)}
                          className="rp-checkbox"
                        />
                        <div className="rp-resident-picker__item-info">
                          <span className="rp-resident-picker__item-name">
                            {r.resident_first_name} {r.resident_last_name}
                          </span>
                          <span className="rp-resident-picker__item-area">
                            {r.area_name} ({r.area_type_description})
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Footer with actions */}
          <div className="rp-form__footer">
            {/* Delete button (edit mode only) */}
            {isEditMode && !showDeleteConfirm && (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="rp-btn rp-btn--danger"
              >
                <i className="fe fe-trash-2" aria-hidden="true" />
                Delete
              </button>
            )}

            {/* Delete confirmation */}
            {isEditMode && showDeleteConfirm && (
              <div className="rp-delete-confirm">
                <span className="rp-delete-confirm__text">
                  Delete <strong>{group?.resident_group_name}</strong>?
                </span>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="rp-btn rp-btn--cancel rp-btn--sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="rp-btn rp-btn--danger-solid rp-btn--sm"
                >
                  Delete
                </button>
              </div>
            )}

            {/* Spacer pushes Cancel/Save right */}
            {!showDeleteConfirm && <div style={{ flex: 1 }} />}

            {!showDeleteConfirm && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rp-btn rp-btn--cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || saving}
                  className="rp-btn rp-btn--primary"
                >
                  {saving ? (
                    <span className="rp-saving-text">
                      <svg className="rp-icon-md rp-spinner" fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    isEditMode ? 'Save Changes' : 'Create Group'
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default GroupDrawer;
